import React, { ReactNode, useCallback } from "react";
import { View } from "react-native";
import { useTranslation } from "locales";
import { StyleFunction, useThemedStyle, StyleData } from "../../theme";
import { Text } from "../text";
import { Button } from "../button";
import { ModalWrapper, useModal } from "../modal";

export function AlertModal({
    headerText,
    bodyText,
    onClose,
}: {
    headerText: string;
    bodyText: ReactNode;
    onClose: (val: boolean) => void;
}) {
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);

    return (
        <ModalWrapper
            onClose={() => onClose(false)}
            showCloseIcon={false}
            title={headerText}
            containerStyle={styles.modalContainer}
        >
            <View
                style={[
                    styles.container,
                    headerText ? styles.resetTopPadding : null,
                ]}
            >
                {typeof bodyText === "string" ||
                typeof bodyText === "number" ? (
                    <Text>{bodyText}</Text>
                ) : (
                    <>{bodyText}</>
                )}
            </View>

            <View style={styles.buttonContainer}>
                <Button
                    type="primary"
                    onPress={() => {
                        onClose(true);
                    }}
                    testID="confirm-button"
                    style={styles.button}
                >
                    {t("common.ok", "OK")}
                </Button>
            </View>
        </ModalWrapper>
    );
}

const styleFunc: StyleFunction = (theme, dimensions) => {
    let styling: StyleData = {
        // Cap the whole modal (not just the body) so that a long header
        // cannot make the modal wider than the body's design width
        // (the body gets a fixed width of
        // dimensions.width - theme.spacingScale * 2 in portrait).
        modalContainer: {
            maxWidth:
                dimensions.width <= theme.breakpoint.portraitWidth
                    ? dimensions.width - theme.spacingScale * 2
                    : 450,
        },
        container: {
            padding: theme.spacingScale * 2,
            // Only effective in landscape; in portrait the block below
            // replaces this with a fixed width and maxWidth: "auto".
            maxWidth: 450,
        },
        buttonContainer: {
            ...theme.styles.row,
        },

        button: {
            flex: 1,
            borderRadius: 0,
            borderBottomLeftRadius: theme.borderRadius,
            borderBottomRightRadius: theme.borderRadius,
        },

        resetTopPadding: {
            paddingTop: 0,
        },
    };

    if (dimensions.width <= theme.breakpoint.portraitWidth) {
        styling = {
            ...styling,

            container: {
                ...styling.container,
                width: dimensions.width - theme.spacingScale * 2,
                maxWidth: "auto",
            },
        };
    }

    return styling;
};

export function useAlert() {
    const { render } = useModal();

    const alert = useCallback(
        (headerText: string, bodyText: ReactNode) =>
            render(onClose => (
                <AlertModal
                    headerText={headerText}
                    bodyText={bodyText}
                    onClose={onClose}
                />
            )),
        [render]
    );

    return alert;
}
